/**
 * Chromium can emit `beforeinstallprompt` shortly after `load`. The Angular
 * application starts only after Native Federation has downloaded and
 * initialized its manifest, so registering the listener from an injectable
 * service is too late on a fast mobile connection.
 *
 * index.html captures the event before any application script runs. This
 * module connects that early state to Angular before federation initialization
 * starts. Unit tests and hosts without index.html use its direct-listener
 * fallback.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type PromptListener = (event: BeforeInstallPromptEvent | null) => void;

interface EarlyInstallPromptCapture {
  prompt: BeforeInstallPromptEvent | null;
  installed: boolean;
}

type WindowWithInstallCapture = Window & {
  __kaitoPwaInstallCapture?: EarlyInstallPromptCapture;
};

const earlyCapture =
  typeof window === 'undefined'
    ? undefined
    : (window as WindowWithInstallCapture).__kaitoPwaInstallCapture;
let capturedPrompt: BeforeInstallPromptEvent | null = earlyCapture?.prompt ?? null;
let installedSincePageLoad = earlyCapture?.installed ?? false;
const listeners = new Set<PromptListener>();

function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent || '');
}

function notifyListeners(): void {
  for (const listener of listeners) {
    listener(capturedPrompt);
  }
}

function handleBeforeInstallPrompt(event: Event): void {
  // Do not suppress the native omnibox/menu affordance on desktop. The custom
  // install banner is an Android-only experience.
  if (!isAndroid()) return;

  event.preventDefault();
  capturedPrompt = event as BeforeInstallPromptEvent;
  notifyListeners();
}

function handleAppInstalled(): void {
  installedSincePageLoad = true;
  capturedPrompt = null;
  notifyListeners();
}

function syncEarlyCapture(): void {
  if (!earlyCapture) return;

  capturedPrompt = earlyCapture.prompt;
  installedSincePageLoad = earlyCapture.installed;
  notifyListeners();
}

if (typeof window !== 'undefined') {
  if (earlyCapture) {
    window.addEventListener('kaito:pwa-install-prompt', syncEarlyCapture);
    window.addEventListener('kaito:pwa-app-installed', syncEarlyCapture);
  } else {
    // Unit tests and non-index.html hosts do not create the early bridge.
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
  }
}

export function subscribeToInstallPrompt(listener: PromptListener): () => void {
  listeners.add(listener);
  listener(capturedPrompt);
  return () => listeners.delete(listener);
}

export function clearCapturedInstallPrompt(event: BeforeInstallPromptEvent | null): void {
  if (capturedPrompt === event) {
    capturedPrompt = null;
    if (earlyCapture?.prompt === event) {
      earlyCapture.prompt = null;
    }
  }
}

export function wasInstalledSincePageLoad(): boolean {
  return installedSincePageLoad;
}
